import React, { useRef, useState, useEffect } from 'react'

import { Alert, Button, CircularProgress, Grid, IconButton, ListItemText, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { CancelRounded, FileDownloadOutlined, StarRateRounded, UploadFileRounded, FiberManualRecordRounded, RectangleRounded, WarningOutlined } from '@mui/icons-material'
import { apiDownloadFile, apiGet, apiImportFile } from '../generic/Api_Functions'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import createDialogTitle from '../generic/utilities/dialogUtil'
import CloseIcon from '@mui/icons-material/Close'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import theme from '../../themeConfig'

const ImportFile = ({ columns, beanParams, disabled, selectedIndex = 0, menuItemButton = false }) => {
  const fileInputRef = useRef()
  const closeDialogTimeoutRef = useRef()
  const [dialogShow, setDialogShow] = useState(false)
  const [showPreImportDialog, setShowPreImportDialog] = useState(false)
  const [fields, setFields] = useState([])
  const [activeIndex, setActiveIndex] = useState(selectedIndex)
  const [lastErrorMessage, setLastErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [closingOnSuccess, setClosingOnSuccess] = useState(false)
  const [csvSeparator, setCsvSeparator] = useState(';')
  const { t_ } = useSmartTranslation()

  useEffect(() => {
    apiGet('importFields', beanParams[activeIndex].bean).then((result) => {
      setFields(result)
    })
    apiGet('settings/character').then((result) => {
      if (result[0].length !== 0) {
        setCsvSeparator(result[0])
      }
    })
  }, [beanParams, activeIndex])

  useEffect(() => {
    return () => {
      clearTimeout(closeDialogTimeoutRef.current)
    }
  }, [])

  const handleChange = ({ target: { files } }) => {
    setLastErrorMessage(false)
    importCSV(files[0])
  }

  const importCSV = async (newFile) => {
    setLoading(true)
    const response = await apiImportFile('import', beanParams[activeIndex], newFile)
      .then(async data => {
        setLoading(false)
        if (data.errorCount === 0) {
          // A clean upload is not always a finished import: a two-phase bean (the BOM library) only
          // PARKS the lines here and does the real write in `postImportAction`. Awaiting the hook and
          // letting it answer `false` is what stops us telling the user it went well when nothing
          // landed — the PO's rule of 2026-09-15: if there is a green, the data is there.
          // Backward compatible: a hook that returns nothing counts as success, like before.
          // What the hook answers decides what the user is told, HERE, in the dialog they are
          // looking at:
          //   undefined -> it imported (every single-phase bean: unchanged behaviour)
          //   false     -> say nothing; the caller reports it its own way
          //   { error } -> show that rejection in this dialog
          const outcome = typeof beanParams[activeIndex]?.postImportAction === 'function'
            ? await beanParams[activeIndex]?.postImportAction()
            : undefined
          if (outcome && outcome.error) {
            setLastErrorMessage({ severity: 'error', message: outcome.error, showButton: false })
          } else if (outcome !== false) {
            setLastErrorMessage({ severity: 'success', message: 'Data imported succesfully. ' + (beanParams[activeIndex]?.importType === 'UPDATE' ? 'Updated ' : 'Added ') + data.okCount + ' rows', showButton: false })
          }
          if (beanParams[activeIndex]?.closeDialogOnSuccess && !(outcome && outcome.error)) {
            setClosingOnSuccess(true)
            clearTimeout(closeDialogTimeoutRef.current)
            closeDialogTimeoutRef.current = setTimeout(() => {
              setDialogShow(false)
              setClosingOnSuccess(false)
            }, 3000)
          }
        } else {
          if (typeof beanParams[activeIndex]?.postImportAction === 'function' && data.okCount > 0) {
            beanParams[activeIndex]?.postImportAction()
          }
          setLastErrorMessage({ severity: 'error', message: 'Some of the data could not be loaded. ' + data.errorCount + '/' + (data.errorCount + data.okCount) + ' failed. Check the error file for details.', errorFileName: data.errorFileName, showButton: true })
        }
      }
      )
      .catch((error) => {
        setLoading(false)
        // A bean may translate the rejection of Sun for its own import (optional `translateError`; default: as it comes)
        const translateError = beanParams[activeIndex]?.translateError
        setLastErrorMessage({ severity: 'error', message: typeof translateError === 'function' ? translateError(error.message) : error.message, showButton: false })
      })
    return response
  }

  const handleUploadProgram = (event) => {
    setLastErrorMessage(false)
    console.warn('%cSi has llegado aquí TOP LOVE FOREVER EQUIPO 💖by Kristina ', 'color:violet; font-size:24px')
    fileInputRef.current.click()
  }

  const handleBeanActive = (event, newBeanActiveName) => {
    if (!newBeanActiveName) {
      return
    }
    const newBeanIndex = beanParams.findIndex((element) => element.bean.beanName === newBeanActiveName.beanName && element.bean.serviceName === newBeanActiveName.serviceName)
    if (newBeanIndex !== -1) {
      setActiveIndex(newBeanIndex)
    }
  }

  const handleErrorDownloadButtonClick = async () => {
    const url = 'import/error_file/' + lastErrorMessage.errorFileName
    try {
      await apiDownloadFile(url, lastErrorMessage.errorFileName)
    } catch (error) {
      console.error('error downloading file:', error)
    }
  }

  const downloadTemplate = () => {
    let output = ''
    fields.forEach(element => {
      output = output + element.fieldName + csvSeparator
    })
    // remove last ';'
    output = output.substring(0, output.length - 1)

    // Generate blob, url and download file with the content above
    const blob = new Blob([output])
    const fileDownloadUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.download = beanParams[activeIndex].name + '_template.csv'
    link.href = fileDownloadUrl
    link.click()
  }

  const dialogRender = (event) => {
    let mainContent
    if (loading) {
      return <CircularProgress />
    } else {
      mainContent = (
        <>
          {lastErrorMessage
            ? (
              <Alert
                severity={lastErrorMessage.severity} action={lastErrorMessage.showButton
                  ? (
                    <>
                      <Button color='inherit' size='small' variant='outlined' onClick={handleErrorDownloadButtonClick}>
                        Download error file
                      </Button>
                      <IconButton size='small' onClick={() => { setLastErrorMessage(false) }}><CloseIcon /></IconButton>
                    </>
                    )
                  : null}
              > {lastErrorMessage.message}
              </Alert>
              )
            : null}
          <Grid
            container direction='column'
            justifyContent='center'
            spacing={2}
          >
            <Grid item>
              <ToggleButtonGroup
                value={beanParams[activeIndex].bean}
                aria-label='text formatting'
                sx={{ bgcolor: 'background.paper' }}
                onChange={handleBeanActive}
                exclusive
              >
                {beanParams !== undefined
                  ? beanParams.map((element) =>
                    <ToggleButton value={element.bean} aria-label={element.bean.beanName} color='primary' key={'bean-' + element.bean.beanName}>
                      {element.name}
                    </ToggleButton>
                  )
                  : null}
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t_('Column Name')}</TableCell>
                  <TableCell>{t_('Type')}</TableCell>
                  <TableCell>{t_('Format')}</TableCell>
                  <TableCell>{t_('Required')}</TableCell>
                  <TableCell>{t_('Default value')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((data) =>
                  <TableRow key={beanParams[activeIndex].id + '-field-' + data.fieldName}>
                    <TableCell>{data.fieldName}</TableCell>
                    <TableCell>{data.type}</TableCell>
                    <TableCell>{data.format}</TableCell>
                    <TableCell>
                      <div style={{ lineHeight: '0' }}>
                        {data.required && <StarRateRounded />}
                        {['length_x', 'length_y', 'length_z'].includes(data.fieldName)
                          ? (
                            <>
                              <StarRateRounded />
                              <RectangleRounded />
                            </>
                            )
                          : ['diameter', 'length'].includes(data.fieldName)
                              ? (
                                <>
                                  <StarRateRounded />
                                  <FiberManualRecordRounded />
                                </>
                                )
                              : null}
                      </div>
                    </TableCell>
                    <TableCell>{data.defaultValue}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )
    }

    return (mainContent)
  }

  const handleOpenDialog = () => {
    clearTimeout(closeDialogTimeoutRef.current)
    setDialogShow(true)
    setLastErrorMessage(false)
    setClosingOnSuccess(false)
  }

  const handleCloseDialog = () => {
    clearTimeout(closeDialogTimeoutRef.current)
    setDialogShow(false)
    setClosingOnSuccess(false)
  }

  return (
    <>
      <SmartDialog
        title={{
          icon: <WarningOutlined color='error' />,
          render: (<Typography variant='h4' color='error'>{t_('Warning')}</Typography>)
        }}
        renderComponent={beanParams[activeIndex]?.preImportMessage}
        setOpen={showPreImportDialog}
        acceptCallback={() => { setShowPreImportDialog(false); handleUploadProgram() }}
        cancelCallback={() => { setShowPreImportDialog(false) }}
      />
      {menuItemButton
        ? (
          <MenuItem onClick={() => handleOpenDialog()} disabled={disabled}>
            <IconButton sx={{ pointerEvents: 'none' }}>
              <UploadFileRounded />
            </IconButton>
            <ListItemText
              primary={t_('IMPORT FILE').split(' ').map(record => record.charAt(0) + record.substring(1, record.length).toLowerCase()).join(' ')}
              sx={{ color: theme.palette.primary.main }}
            />
          </MenuItem>
          )
        : (
          <Button
            sx={{ borderColor: 'transparent' }}
            color='primary'
            variant='outlined'
            onClick={() => handleOpenDialog()}
            disabled={disabled}
          >
            <UploadFileRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_(beanParams[activeIndex].title ? beanParams[activeIndex].title : 'IMPORT FILE')}</Typography>
          </Button>)}
      <input
        id='changeProgram'
        onClick={(event) => {
          event.target.value = null
        }}
        onChange={handleChange}
        multiple
        ref={fileInputRef}
        type='file'
        hidden
      />
      <SmartDialog
        acceptCallbackArgsArray={[columns]} acceptCallback={() => handleUploadProgram()}
        renderComponent={dialogRender()}
        title={createDialogTitle({ title: t_('Import File'), icon: UploadFileRounded })} setOpen={dialogShow}
        accept={false}
        cancel={false}
        renderCustomButtons={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Button
              sx={{
                border: '1px solid rgba(255, 255, 255, 0.23) !important',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08) !important'
                }
              }} variant='outlined' onClick={() => { downloadTemplate() }} disabled={closingOnSuccess}
            >
              <Typography sx={{ color: 'lightgrey' }}>{t_('TEMPLATE')}</Typography>
              <FileDownloadOutlined sx={{ fontSize: '1.4em', marginLeft: '0.3rem', color: 'lightgrey' }} />
            </Button>
            <Button
              variant='outlined' sx={{
                border: '1px solid rgba(255, 255, 255, 0.23) !important',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08) !important'
                },
                marginLeft: '5px'
              }} onClick={() => handleCloseDialog()} disabled={loading || closingOnSuccess}
            >
              <CancelRounded sx={{ color: 'lightgrey !important', fontSize: '1.4em', marginRight: '0.3rem' }} />
              {/* `cancelLabel` lets a caller name the button for its own flow (e.g. 'Close' when the
                  import commits by itself and there is nothing to cancel). Default unchanged. */}
              <Typography sx={{ color: 'lightgrey' }}>{t_(beanParams[activeIndex]?.cancelLabel || 'CANCEL')}</Typography>
            </Button>
            <Button variant='outlined' sx={{ marginLeft: '5px' }} onClick={() => beanParams[activeIndex]?.preImportMessage ? setShowPreImportDialog(true) : handleUploadProgram()} disabled={loading || closingOnSuccess}>
              <Typography>{t_('IMPORT FILE')} </Typography>
              <UploadFileRounded sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
            </Button>
          </div>
        }
      />
    </>
  )
}
export default ImportFile
